import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { FaEdit, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import Swal from "sweetalert2";
import CommonFormHeading from "../CommonUtils/CommonFormHeading";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { failureAlert, successAlert2 } from "../CommonUtils/SweetAlerts";
import { config } from "../CommonUtils/CommonURLS";

const contactUsValidationSchema = Yup.object().shape({
    address: Yup.string().trim().required("Address is required").min(10, "Address should be at least 10 characters").max(500, "Address should not exceed 500 characters"),
    mobile: Yup.string().trim().required("Mobile number is required").matches(/^[6-9]\d{9}$/, "Enter a valid 10 digit mobile number"),
    email: Yup.string().trim().required("Email is required").email("Enter a valid email address").max(100, "Email should not exceed 100 characters"),
});

const createEmptyContactUsData = () => ({
    address: "",
    mobile: "",
    email: "",
});

export default function ContactUs() {
    const [contactUsRecord, setContactUsRecord] = useState(null);
    const [isContactUsLoading, setIsContactUsLoading] = useState(true);
    const [isContactUsEditMode, setIsContactUsEditMode] = useState(false);

    const contactUsFormik = useFormik({
        initialValues: createEmptyContactUsData(),
        validationSchema: contactUsValidationSchema,
        onSubmit: (contactUsFormValues) => {
            Swal.fire({
                text: "Are you sure you want to save contact details?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, save it!",
                cancelButtonText: "No, cancel!",
                backdrop: false,
            }).then((result) => {
                if (!result.isConfirmed) {
                    return;
                }

                const isUpdate = Boolean(contactUsRecord?.sno);
                const contactUsSubmitUrl = isUpdate ? `${config.url.STU_URL}updateContactUs` : `${config.url.STU_URL}saveContactUsEntry`;
                const contactUsSubmitPayload = {
                    // contactUsEntryList: [
                    //     {
                    ...(isUpdate ? { sno: contactUsRecord.sno } : {}),
                    address: contactUsFormValues.address,
                    mobile: contactUsFormValues.mobile,
                    email: contactUsFormValues.email,
                    //     },
                    // ],
                };

                CommonAxiosPost(contactUsSubmitUrl, contactUsSubmitPayload).then((response) => {
                    if (response?.data?.scode === "01") {
                        successAlert2(response?.data?.sdesc).then((isConfirmed) => {
                            if (isConfirmed) {
                                setIsContactUsEditMode(false);
                                fetchContactUsRecords();
                            }
                        });
                    } else {
                        failureAlert(response?.data?.sdesc || "Unable to save contact details");
                    }
                });
            });
        },
    });

    const fetchContactUsRecords = () => {
        setIsContactUsLoading(true);
        const contactUsFetchUrl = `${config.url.STU_URL}ov-public/getContactUs`;

        CommonAxiosGet(contactUsFetchUrl)
            .then((response) => {
                if (response?.data?.scode === "01") {
                    const contactData = Array.isArray(response?.data?.data) ? response.data.data[0] : null;
                    setContactUsRecord(contactData || null);
                } else {
                    setContactUsRecord(null);
                }
            })
            .finally(() => {
                setIsContactUsLoading(false);
            });
    };

    useEffect(() => {
        fetchContactUsRecords();
    }, []);

    const handleEditContactUsRecord = () => {
        contactUsFormik.setValues({
            address: contactUsRecord?.address || "",
            mobile: contactUsRecord?.mobile_no || "",
            email: contactUsRecord?.email_id || "",
        });
        setIsContactUsEditMode(true);
    };

    const handleCancelEdit = () => {
        setIsContactUsEditMode(false);
        contactUsFormik.resetForm({ values: createEmptyContactUsData() });
    };

    return (
        <div className="affl-newdiv">
            <div className="container-fluid px-1 mt-3">
                <CommonFormHeading heading="Contact Us" />

                {isContactUsLoading ? (
                    <div className="container shadow-none px-3 m-0 mw-100 pb-5" style={{ background: "linear-gradient(180deg, #f6f4ee 0%, #f3f0e8 100%)", borderRadius: "12px" }}>
                        <div className="card border-0 shadow-sm p-4 mt-3" style={{ borderRadius: "14px", backgroundColor: "#fff" }}>
                            <div className="placeholder-glow mb-3">
                                <span className="placeholder col-3" style={{ height: "20px", borderRadius: "8px" }}></span>
                            </div>
                            <div className="placeholder-glow mb-3">
                                <span className="placeholder col-12" style={{ height: "16px", borderRadius: "8px" }}></span>
                            </div>
                            <div className="row g-3">
                                <div className="col-md-6 col-12 placeholder-glow">
                                    <span className="placeholder col-8" style={{ height: "14px", borderRadius: "8px" }}></span>
                                    <span className="placeholder col-6 mt-2" style={{ height: "14px", borderRadius: "8px", display: "block" }}></span>
                                </div>
                                <div className="col-md-6 col-12 placeholder-glow">
                                    <span className="placeholder col-8" style={{ height: "14px", borderRadius: "8px" }}></span>
                                    <span className="placeholder col-6 mt-2" style={{ height: "14px", borderRadius: "8px", display: "block" }}></span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="container shadow-none px-3 m-0 mw-100 pb-5" style={{ background: "linear-gradient(180deg, #f6f4ee 0%, #f3f0e8 100%)", borderRadius: "12px" }}>
                        {!isContactUsEditMode && contactUsRecord && (
                            <div className="card border-0 shadow-sm p-4 mb-3 mt-3" style={{ backgroundColor: "#fff", borderRadius: "14px" }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0" style={{ color: "#1f2937", fontWeight: 700 }}>Contact Details</h5>
                                    <Button type="button" size="sm" variant="primary" onClick={handleEditContactUsRecord}>
                                        <FaEdit className="me-1" />
                                        Edit Contact
                                    </Button>
                                </div>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="fw-semibold mb-1 d-flex align-items-center gap-2" style={{ color: "#334155", fontSize: "0.95rem" }}>
                                            <FaMapMarkerAlt style={{ color: "#d97706" }} />
                                            <span>Address</span>
                                        </div>
                                        <div className="p-3" style={{ whiteSpace: "pre-line", backgroundColor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "10px", color: "#111827" }}>
                                            {contactUsRecord.address || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-6 col-12">
                                        <div className="p-3 h-100" style={{ backgroundColor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "10px" }}>
                                            <div className="fw-semibold mb-1 d-flex align-items-center gap-2" style={{ color: "#334155", fontSize: "0.95rem" }}>
                                                <FaPhoneAlt style={{ color: "#d97706" }} />
                                                <span>Mobile Number</span>
                                            </div>
                                            <div style={{ color: "#111827", fontWeight: 500 }}>{contactUsRecord.mobile_no || "-"}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 col-12">
                                        <div className="p-3 h-100" style={{ backgroundColor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "10px" }}>
                                            <div className="fw-semibold mb-1 d-flex align-items-center gap-2" style={{ color: "#334155", fontSize: "0.95rem" }}>
                                                <FaEnvelope style={{ color: "#d97706" }} />
                                                <span>Email Id</span>
                                            </div>
                                            <div style={{ color: "#111827", fontWeight: 500, wordBreak: "break-word" }}>{contactUsRecord.email_id || "-"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {(isContactUsEditMode || !contactUsRecord) && (
                            <FormikProvider value={contactUsFormik}>
                                <Form onSubmit={contactUsFormik.handleSubmit}>
                                    <div className="card border-0 shadow-sm p-4 mt-3" style={{ backgroundColor: "#fff", borderRadius: "14px" }}>
                                        <h5 className="mb-3" style={{ color: "#1f2937", fontWeight: 700 }}>{contactUsRecord ? "Edit Contact Details" : "Add Contact Details"}</h5>

                                        <div className="mb-3">
                                            <label className="form-label fw-semibold d-flex align-items-center gap-2" style={{ color: "#334155" }}>
                                                <FaMapMarkerAlt style={{ color: "#d97706" }} />
                                                <span>Address</span>
                                            </label>
                                            <textarea
                                                rows={4}
                                                className="form-control"
                                                name="address"
                                                value={contactUsFormik.values.address}
                                                onChange={contactUsFormik.handleChange}
                                                placeholder="Enter address"
                                                // placeholder={"Ex: Xxxxxxxx Xxxxxxxxxx, D.No:XX-XXX,\nXnd & Xrd Floor, Xxxxxx Road XX-XX,\nXxxxxxx Ward-XX, Xxxxxxxx-XXXXXX, Xxxxxx."}
                                                maxLength={500}
                                                style={{ borderRadius: "10px", borderColor: "#d1d5db" }}
                                            />
                                            <ErrorMessage name="address" component="div" className="text-error" />
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6 col-12 mb-3">
                                                <label className="form-label fw-semibold d-flex align-items-center gap-2" style={{ color: "#334155" }}>
                                                    <FaPhoneAlt style={{ color: "#d97706" }} />
                                                    <span>Mobile Number</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="mobile"
                                                    value={contactUsFormik.values.mobile}
                                                    onChange={(event) => {
                                                        const digitsOnlyValue = event.target.value.replace(/\D/g, "").slice(0, 10);
                                                        contactUsFormik.setFieldValue("mobile", digitsOnlyValue);
                                                    }}
                                                    placeholder="Enter mobile number"
                                                    maxLength={10}
                                                    style={{ borderRadius: "10px", borderColor: "#d1d5db" }}
                                                />
                                                <ErrorMessage name="mobile" component="div" className="text-error" />
                                            </div>

                                            <div className="col-md-6 col-12 mb-3">
                                                <label className="form-label fw-semibold d-flex align-items-center gap-2" style={{ color: "#334155" }}>
                                                    <FaEnvelope style={{ color: "#d97706" }} />
                                                    <span>Email Id</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    name="email"
                                                    value={contactUsFormik.values.email}
                                                    onChange={contactUsFormik.handleChange}
                                                    placeholder="Enter email"
                                                    maxLength={100}
                                                    style={{ borderRadius: "10px", borderColor: "#d1d5db" }}
                                                />
                                                <ErrorMessage name="email" component="div" className="text-error" />
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                            {contactUsRecord && (
                                                <Button type="button" size="sm" variant="secondary" onClick={handleCancelEdit}>
                                                    Cancel
                                                </Button>
                                            )}
                                            <Button type="submit" size="sm" variant="success">
                                                {contactUsRecord ? "Update" : "Submit"}
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                            </FormikProvider>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}